import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
  ICP_EMPLOYEE_COUNT_DISTRIBUTION_QUERY,
  ICP_EMPLOYEE_COUNT_WIN_LOSS_DISTRIBUTION_QUERY,
  ICP_GEOGRAPHY_DISTRIBUTION_QUERY,
  ICP_GEOGRAPHY_WIN_LOSS_DISTRIBUTION_QUERY,
  ICP_INDUSTRY_DISTRIBUTION_QUERY,
  ICP_REVENUE_DISTRIBUTION_QUERY,
  ICP_REVENUE_WIN_LOSS_DISTRIBUTION_QUERY,
  ICP_WIN_LOSS_INDUSTRY_DISTRIBUTION_QUERY,
  ICPDistributionVariables,
  ICPEmployeeCountDistributionPayload,
  ICPEmployeeCountWinLossDistributionPayload,
  ICPGeographyDistributionPayload,
  ICPGeographyWinLossDistributionPayload,
  ICPIndustryDistributionPayload,
  ICPRevenueDistributionPayload,
  ICPRevenueWinLossDistributionPayload,
  ICPWinLossIndustryDistributionPayload,
} from "../apis/companySearch/icpDistribution";
import { CompanyMatchFilter } from "../legacy/types/companyFilter";
import { COMPANY_SEARCH_CLIENT } from "../utils/consts";
import { GlobalSettings } from "../types/globalSettings";

interface ICPDistributionHookOptions {
  productIds?: number[];
  matchFile?: CompanyMatchFilter;
  globalSettings?: GlobalSettings;
  skip?: boolean;
}

/**
 * Hook to fetch ICP industry distribution data
 */
export const useICPIndustryDistribution = ({
  productIds,
  matchFile,
  globalSettings,
  skip = false,
}: ICPDistributionHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPIndustryDistributionPayload,
    ICPDistributionVariables
  >(ICP_INDUSTRY_DISTRIBUTION_QUERY, {
    variables: { productIds, matchFile, globalSettings },
    skip: skip || (productIds?.length === 0 && !matchFile),
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const industryDistribution = useMemo(
    () => data?.icpIndustryDistribution || [],
    [data]
  );

  return {
    industryDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP geography distribution data
 */
export const useICPGeographyDistribution = ({
  productIds,
  matchFile,
  globalSettings,
  skip = false,
}: ICPDistributionHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPGeographyDistributionPayload,
    ICPDistributionVariables
  >(ICP_GEOGRAPHY_DISTRIBUTION_QUERY, {
    variables: { productIds, matchFile, globalSettings },
    skip: skip || (productIds?.length === 0 && !matchFile),
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const geographyDistribution = useMemo(
    () => data?.icpGeographyDistribution || [],
    [data]
  );

  return {
    geographyDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP revenue distribution data
 */
export const useICPRevenueDistribution = ({
  productIds,
  matchFile,
  globalSettings,
  skip = false,
}: ICPDistributionHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPRevenueDistributionPayload,
    ICPDistributionVariables
  >(ICP_REVENUE_DISTRIBUTION_QUERY, {
    variables: { productIds, matchFile, globalSettings },
    skip: skip || (productIds?.length === 0 && !matchFile),
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const revenueDistribution = useMemo(
    () => data?.icpRevenueDistribution || [],
    [data]
  );

  return {
    revenueDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP employee count distribution data
 */
export const useICPEmployeeCountDistribution = ({
  productIds,
  matchFile,
  globalSettings,
  skip = false,
}: ICPDistributionHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPEmployeeCountDistributionPayload,
    ICPDistributionVariables
  >(ICP_EMPLOYEE_COUNT_DISTRIBUTION_QUERY, {
    variables: { productIds, matchFile, globalSettings },
    skip: skip || (productIds?.length === 0 && !matchFile),
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const employeeCountDistribution = useMemo(
    () => data?.icpEmployeeCountDistribution || [],
    [data]
  );

  return {
    employeeCountDistribution,
    loading,
    error,
  };
};

interface ICPWinLossHookOptions {
  matchFile: CompanyMatchFilter;
  globalSettings?: GlobalSettings;
  skip?: boolean;
}

/**
 * Hook to fetch ICP win/loss industry distribution data
 */
export const useICPWinLossIndustryDistribution = ({
  matchFile,
  globalSettings,
  skip = false,
}: ICPWinLossHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPWinLossIndustryDistributionPayload,
    { matchFile: CompanyMatchFilter; globalSettings?: GlobalSettings }
  >(ICP_WIN_LOSS_INDUSTRY_DISTRIBUTION_QUERY, {
    variables: { matchFile, globalSettings },
    skip: skip || !matchFile,
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const winLossDistribution = useMemo(
    () => data?.icpWinLossIndustryDistribution || [],
    [data]
  );

  return {
    winLossDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP win/loss geography distribution data
 */
export const useICPWinLossGeographyDistribution = ({
  matchFile,
  globalSettings,
  skip = false,
}: ICPWinLossHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPGeographyWinLossDistributionPayload,
    { matchFile: CompanyMatchFilter; globalSettings?: GlobalSettings }
  >(ICP_GEOGRAPHY_WIN_LOSS_DISTRIBUTION_QUERY, {
    variables: { matchFile, globalSettings },
    skip: skip || !matchFile,
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const winLossDistribution = useMemo(
    () => data?.icpGeographyWinLossDistribution || [],
    [data]
  );

  return {
    winLossDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP win/loss revenue distribution data
 */
export const useICPWinLossRevenueDistribution = ({
  matchFile,
  globalSettings,
  skip = false,
}: ICPWinLossHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPRevenueWinLossDistributionPayload,
    { matchFile: CompanyMatchFilter; globalSettings?: GlobalSettings }
  >(ICP_REVENUE_WIN_LOSS_DISTRIBUTION_QUERY, {
    variables: { matchFile, globalSettings },
    skip: skip || !matchFile,
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const winLossDistribution = useMemo(
    () => data?.icpRevenueWinLossDistribution || [],
    [data]
  );

  return {
    winLossDistribution,
    loading,
    error,
  };
};

/**
 * Hook to fetch ICP win/loss employee count distribution data
 */
export const useICPWinLossEmployeeCountDistribution = ({
  matchFile,
  globalSettings,
  skip = false,
}: ICPWinLossHookOptions) => {
  const { data, loading, error } = useQuery<
    ICPEmployeeCountWinLossDistributionPayload,
    { matchFile: CompanyMatchFilter; globalSettings?: GlobalSettings }
  >(ICP_EMPLOYEE_COUNT_WIN_LOSS_DISTRIBUTION_QUERY, {
    variables: { matchFile, globalSettings },
    skip: skip || !matchFile,
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const winLossDistribution = useMemo(
    () => data?.icpEmployeeCountWinLossDistribution || [],
    [data]
  );

  return {
    winLossDistribution,
    loading,
    error,
  };
};
